import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, TrendingDown, MapPin, ChevronRight, Heart, HeartHandshake } from "lucide-react";
import api from "@/lib/api";
import { useBranding } from "@/context/BrandingContext";

export default function Network() {
  const { money, branding } = useBranding();
  const navigate = useNavigate();
  const [view, setView] = useState("captured"); // captured | unaccounted
  const [regionFilter, setRegionFilter] = useState(null);
  const { data } = useQuery({
    queryKey: ["network-rollup"],
    queryFn: () => api.get("/network/rollup").then((r) => r.data),
  });
  const { data: giving } = useQuery({
    queryKey: ["giving-summary"],
    queryFn: () => api.get("/giving/summary").then((r) => r.data),
  });

  const captured = view === "captured";
  const green = branding.accent_color || "#10B981";
  const red = branding.primary_color;
  const barColor = captured ? green : red;
  const target = branding.target || "sportclips";
  // Clarify region labels so the Sport Clips "Canada" group isn't confused with
  // the US directional regions (and vice-versa in Chatters mode).
  const regionLabel = (region) => {
    if (!region) return region;
    if (target === "newzealand") return region; // NZ rollup groups by brand
    if (target === "justcuts") return region; // Just Cuts groups by country (Australia / Canada)
    if (region === "Canada") return "Canada";
    return target === "chatters" ? `Canada · ${region}` : `US · ${region}`;
  };

  const allStores = [...(data?.stores || [])].sort((a, b) =>
    captured ? b.reconciled - a.reconciled : b.leakage - a.leakage);
  const stores = regionFilter ? allStores.filter((s) => s.region === regionFilter) : allStores;
  const regions = [...(data?.regions || [])].sort((a, b) =>
    captured ? b.reconciled - a.reconciled : b.leakage - a.leakage);
  const maxVal = Math.max(1, ...stores.map((s) => (captured ? s.reconciled : s.leakage)));
  const openStore = (number) => navigate(`/floor?store=${number}&from=network`);

  const Toggle = () => (
    <div className="inline-flex rounded-lg border border-[#2A3550] bg-[#1E2638] p-1" data-testid="network-view-toggle">
      {[["captured", "Sales captured"], ["unaccounted", "Unaccounted"]].map(([k, label]) => (
        <button
          key={k}
          onClick={() => setView(k)}
          data-testid={`network-view-${k}`}
          className={`px-4 py-1.5 text-sm rounded-md transition-colors duration-200 ${view === k ? "text-white" : "text-slate-400 hover:text-white"}`}
          style={view === k ? { backgroundColor: k === "captured" ? green : red } : {}}
        >
          {label}
        </button>
      ))}
    </div>
  );

  return (
    <div>
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">Network Rollup · Revenue Health</div>
          <h1 className="mt-2 font-display text-3xl md:text-4xl font-light tracking-tight">
            {captured ? "Your whole network, ranked by sales captured" : "Your whole network, ranked by what's unaccounted"}
          </h1>
        </div>
        <div className="text-right">
          <div className="text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">
            {captured ? "Total sales captured" : "Total unaccounted"}
          </div>
          <div className="font-display text-3xl font-light" style={{ color: barColor }} data-testid="network-total">
            {data ? money(captured ? data.total_reconciled : data.total_leakage) : "—"}
          </div>
        </div>
      </div>

      <div className="mt-6"><Toggle /></div>

      {/* Region cards — click to filter the store ranking by region */}
      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-5">
        {regions.map((r, i) => (
          <motion.div
            key={r.region}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
            onClick={() => setRegionFilter(regionFilter === r.region ? null : r.region)}
            role="button" tabIndex={0}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setRegionFilter(regionFilter === r.region ? null : r.region)}
            title={`Filter stores in ${r.region}`}
            className={`surface-card p-6 cursor-pointer transition-all duration-200 hover:border-[#38BDF8] ${regionFilter === r.region ? "ring-2 ring-[#6366F1]" : ""}`}
            data-testid={`region-card-${r.region}`}
          >
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">
              <MapPin className="h-3.5 w-3.5" /> {regionLabel(r.region)}
            </div>
            <div className="mt-3 font-display text-2xl font-light" style={{ color: barColor }}>
              {money(captured ? r.reconciled : r.leakage)}
            </div>
            <div className="mt-1 text-sm text-slate-400">
              {captured ? `${r.stores} stores · sales captured` : `${r.flagged} flags · ${r.stores} stores`}
            </div>
          </motion.div>
        ))}
      </div>

      {/* Community giving across the network */}
      {giving && giving.total_count > 0 && (
        <div className="mt-6 surface-card p-6" data-testid="network-giving">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.15em] text-slate-400 font-semibold">
              <HeartHandshake className="h-4 w-4 text-rose-400" /> Community giving across the network
            </div>
            <div className="text-right">
              <div className="font-display text-2xl font-light text-rose-300" data-testid="network-giving-total">{money(giving.total_donated)}</div>
              <div className="text-xs text-slate-400">{giving.total_count} free cuts donated</div>
            </div>
          </div>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {giving.beneficiaries.map((b) => (
              <div key={b.beneficiary} className="rounded-lg border border-[#2A3550] bg-[#171E2E] px-4 py-3">
                <div className="text-sm font-medium truncate flex items-center gap-2"><Heart className="h-3.5 w-3.5 text-rose-400 shrink-0" />{b.beneficiary}</div>
                <div className="mt-1 flex items-center justify-between text-xs text-slate-400">
                  <span>{b.count} cut{b.count !== 1 ? "s" : ""}</span>
                  <span className="text-rose-300 font-medium">{money(b.donated)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Store ranking — click a store to open its live floor */}
      <div className="mt-6 surface-card p-6">
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <h2 className="font-display text-lg font-medium">
            {captured ? "Stores ranked by sales captured" : "Stores ranked by unaccounted revenue"}
          </h2>
          {regionFilter && (
            <button onClick={() => setRegionFilter(null)} data-testid="clear-region-filter"
              className="inline-flex items-center gap-1.5 text-xs rounded-full border border-[#2A3550] bg-[#1E2638] px-3 py-1 text-slate-300 hover:border-[#38BDF8] transition-colors duration-200">
              {regionLabel(regionFilter)} · clear ✕
            </button>
          )}
        </div>
        <div className="space-y-1.5" data-testid="store-ranking">
          {stores.map((s, i) => {
            const val = captured ? s.reconciled : s.leakage;
            return (
              <div
                key={s.store_id}
                onClick={() => openStore(s.number)}
                role="button" tabIndex={0}
                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && openStore(s.number)}
                title="Open this store's live floor"
                className="flex items-center gap-4 cursor-pointer rounded-lg -mx-2 px-2 py-1.5 hover:bg-[#171E2E] transition-colors duration-150 group"
                data-testid={`rank-row-${s.number}`}
              >
                <div className="w-6 text-sm text-slate-400 font-mono">{i + 1}</div>
                <div className="w-44 shrink-0">
                  <div className="text-sm font-medium group-hover:text-[#38BDF8] transition-colors duration-150">#{s.number} {s.name}</div>
                  <div className="text-xs text-slate-400">{regionLabel(s.region)} · {s.match_rate}% match</div>
                </div>
                <div className="flex-1 h-8 bg-[#171E2E] rounded-lg overflow-hidden relative">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${(val / maxVal) * 100}%` }}
                    transition={{ duration: 0.7, delay: i * 0.04 }}
                    className="h-full rounded-lg"
                    style={{ backgroundColor: barColor, opacity: 0.9 }}
                  />
                  <div className="absolute inset-0 flex items-center justify-end pr-3 text-sm font-medium">
                    {money(val)}
                  </div>
                </div>
                <div className="w-16 text-right text-xs text-slate-400 flex items-center gap-1 justify-end">
                  {captured
                    ? <><TrendingUp className="h-3.5 w-3.5" style={{ color: green }} /> {s.match_rate}%</>
                    : <><TrendingDown className="h-3.5 w-3.5" /> {s.flagged}</>}
                </div>
                <ChevronRight className="h-4 w-4 text-slate-500 group-hover:text-[#38BDF8] transition-colors duration-150" />
              </div>
            );
          })}
          {stores.length === 0 && (
            <div className="py-8 text-center text-sm text-slate-400">No stores in {regionLabel(regionFilter)}.</div>
          )}
        </div>
      </div>
    </div>
  );
}
